import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import {
  selectIsFrequencyManageCappingFlagOn,
  selectIsFrequencyManageOptimizeFlagOn,
} from 'core/userContext';
import {
  CoreContext,
  defaultContext,
  defaultExportData,
} from 'modules/frequencyManagement/contexts/CoreContext';
import { optimizeCSVDataHeadersMapping } from 'modules/frequencyManagement/contexts/contextUtils';
import {
  CsvData,
  FrequencyManagementMessageType,
  FrequencyManagementSettings,
  FrequencyMode,
  HistogramDataPoint,
  SupportedMediums,
} from 'modules/frequencyManagement/lib/types';
import {
  parseFrequencyManagementResponse,
  supportedMediums,
} from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import { noop } from 'utils/noop';
import { formatOptimizeLastUpdatedDate } from 'modules/frequencyManagement/commonUtils';
import { useFetchFrequencyManagement } from '../hooks/useFetchFrequencyManagement';

export const CoreProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const isFrequencyManagementCappingEnabled = useSelector(selectIsFrequencyManageCappingFlagOn);
  const isFrequencyManagementOptimizeEnabled = useSelector(selectIsFrequencyManageOptimizeFlagOn);

  const { refetch, data, error, isLoading } = useFetchFrequencyManagement({
    skip: !isFrequencyManagementCappingEnabled,
  });
  const parsedFrequencyManagementData = data
    ? parseFrequencyManagementResponse(data)
    : defaultContext.frequencyManagementData;

  const parseHistogramDetails = (
    optimizeFields: FrequencyManagementSettings | null | undefined,
  ) => {
    return {
      histogramData: optimizeFields?.frequencyOptimizationDetails || {
        histogram_data_points: [] as HistogramDataPoint[],
        csv_export_data: defaultExportData,
      },
      lastUpdated: formatOptimizeLastUpdatedDate(
        optimizeFields?.frequencyOptimizationDetailsUpdatedAt || '',
      ),
    };
  };

  const getOptimizeDetailsMessageMedium = (medium: SupportedMediums) => {
    const messageMediumConfig = parsedFrequencyManagementData[medium];
    if (
      !messageMediumConfig.settings ||
      messageMediumConfig.settings.mode !== FrequencyMode.Optimize
    ) {
      return {
        ...parseHistogramDetails(messageMediumConfig.settings),
        areResultsReady: false,
        hasSavedData: false,
      };
    }

    const details = parseHistogramDetails(messageMediumConfig.settings);
    return isEmpty(details.histogramData.histogram_data_points)
      ? {
          ...details,
          areResultsReady: false,
          hasSavedData: true,
        }
      : {
          ...details,
          areResultsReady: true,
          hasSavedData: true,
        };
  };
  const getOptimizeDetailsMessageType = (medium: SupportedMediums, messageTypeId: number) => {
    const messageMediumConfig = parsedFrequencyManagementData[medium];
    if (!messageMediumConfig.settings || !messageMediumConfig.messageTypes) {
      return {
        ...parseHistogramDetails(messageMediumConfig.settings),
        areResultsReady: false,
        hasSavedData: false,
      };
    }
    const matchingMessageTypeSettings = (
      messageMediumConfig.messageTypes as FrequencyManagementMessageType[]
    ).find((messageType) => messageType.id === messageTypeId)?.frequencyManagement.settings;

    if (
      !matchingMessageTypeSettings ||
      matchingMessageTypeSettings.mode !== FrequencyMode.Optimize
    ) {
      return {
        ...parseHistogramDetails(matchingMessageTypeSettings),
        areResultsReady: false,
        hasSavedData: false,
      };
    }
    const details = parseHistogramDetails(matchingMessageTypeSettings);
    return isEmpty(details.histogramData.histogram_data_points)
      ? {
          ...details,
          areResultsReady: false,
          hasSavedData: true,
        }
      : {
          ...details,
          areResultsReady: true,
          hasSavedData: true,
        };
  };

  const getOptimizeModeCountByMedium = (messageMedium: SupportedMediums) => {
    if (!parsedFrequencyManagementData[messageMedium].isConfiguredForSending) return 0;
    let optimizeModeCount = 0;
    const messageMediumConfig = parsedFrequencyManagementData[messageMedium];
    if (
      messageMediumConfig.settings &&
      messageMediumConfig.settings.mode === FrequencyMode.Optimize
    ) {
      optimizeModeCount += 1;
    }
    if (messageMediumConfig && messageMediumConfig.messageTypes) {
      optimizeModeCount += messageMediumConfig.messageTypes.filter(
        (messageType) => messageType.frequencyManagement.settings?.mode === FrequencyMode.Optimize,
      ).length;
    }

    return optimizeModeCount;
  };

  const getOptimizeCSVData = () => {
    const mapExportDataItemToCsv = (
      dataExportEntries: CsvData[],
    ): Array<Record<string, string | number>> => {
      return dataExportEntries.map((dataExportEntry) => {
        return optimizeCSVDataHeadersMapping.reduce(
          (accExportMap, dataMapping) => {
            const mappedItem = dataExportEntry[dataMapping.exportName as keyof CsvData];
            return {
              ...accExportMap,
              [dataMapping.key]: (dataMapping.transform as any)(mappedItem),
            };
          },
          {} as Record<string, string | number>,
        );
      });
    };

    const parsedCsvData = supportedMediums.flatMap((medium) => {
      let csvByMedium: Array<Record<string, string | number>> = [];
      const messageMediumConfig = parsedFrequencyManagementData[medium];
      const mediumIsOptimize =
        get(messageMediumConfig, 'settings.mode', FrequencyMode.Off) === FrequencyMode.Optimize;
      if (mediumIsOptimize) {
        const mediumData = get(
          messageMediumConfig,
          'settings.frequencyOptimizationDetails.csv_export_data',
          {},
        ) as CsvData[];
        if (!isEmpty(mediumData)) {
          csvByMedium = csvByMedium.concat(mapExportDataItemToCsv(mediumData));
        }
      }

      const messageTypes = get(messageMediumConfig, 'messageTypes', []);
      if (isEmpty(messageTypes)) {
        return csvByMedium;
      }

      messageTypes.forEach((messageType: any) => {
        const messageTypeIsOptimize =
          get(messageType, 'frequencyManagement.settings.mode', FrequencyMode.Off) ===
          FrequencyMode.Optimize;
        if (messageTypeIsOptimize) {
          const messageTypeData = get(
            messageType,
            'frequencyManagement.settings.frequencyOptimizationDetails.csv_export_data',
            {},
          );
          if (!isEmpty(messageTypeData)) {
            csvByMedium = csvByMedium.concat(mapExportDataItemToCsv(messageTypeData));
          }
        }
      });

      return csvByMedium;
    });
    return {
      hasCSVData: !isEmpty(parsedCsvData),
      csvData: parsedCsvData,
    };
  };

  return (
    <CoreContext.Provider
      value={{
        fetch: refetch,
        isError: !!error,
        isLoading,
        trigger: isFrequencyManagementCappingEnabled ? refetch : noop,
        frequencyManagementData: parsedFrequencyManagementData,
        getOptimizeDetailsMessageMedium: isFrequencyManagementOptimizeEnabled
          ? getOptimizeDetailsMessageMedium
          : defaultContext.getOptimizeDetailsMessageMedium,
        getOptimizeDetailsMessageType: isFrequencyManagementOptimizeEnabled
          ? getOptimizeDetailsMessageType
          : defaultContext.getOptimizeDetailsMessageType,
        getOptimizeModeCountByMedium: isFrequencyManagementOptimizeEnabled
          ? getOptimizeModeCountByMedium
          : defaultContext.getOptimizeModeCountByMedium,
        getOptimizeCSVData: isFrequencyManagementOptimizeEnabled
          ? getOptimizeCSVData
          : defaultContext.getOptimizeCSVData,
      }}
    >
      {children}
    </CoreContext.Provider>
  );
};
